import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppPagination from "../Tools/AppPagination";
import { motion } from "framer-motion";
import { getMyEnrolledCourses } from "../../State/Learner/Action";
import { generateSlug } from "../Constant/Utils";
import {
  FaBook,
  FaClock,
  FaChalkboardTeacher,
  FaStar,
  FaLayerGroup,
} from "react-icons/fa";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Rating,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { addCourseReview } from "../../State/Review/Action";
import ErrorDialog from "../Tools/ErrorDialog";
import CourseReviews from "../Course/CourseReviews";

const MyEnrolledCourses = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [openCourseDialog, setOpenCourseDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const enrolledCourses =
    useSelector(
      (state) => state.learner?.myEnrolledCourses?.data?.enrolledCourses
    ) || [];

  const courseReviewError = useSelector(
    (state) => state.review?.courseReviewError
  );

  const courseReviewResult = useSelector(
    (state) => state.review?.courseReviewResult
  );

  const totalPages =
    useSelector((state) => state.learner?.mycourses?.data?.totalPages) || 0;

  useEffect(() => {
    dispatch(getMyEnrolledCourses(page));
  }, [dispatch, page, courseReviewResult]);

  const handleOpenReviewDialog = (courseId) => {
    setSelectedCourseId(courseId);
    setOpenReviewDialog(true);
  };

  const handleCloseReviewDialog = () => {
    setOpenReviewDialog(false);
    setSelectedCourseId(null);
  };

  const handleOpenDialog = (course) => {
    setSelectedCourse(course);
    setRatingValue(0);
    setReviewText("");
    setOpenCourseDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCourseDialog(false);
    setRatingValue(0);
    setReviewText("");
    dispatch(getMyEnrolledCourses(page));
  };

  useEffect(() => {
    if (courseReviewError) {
      setShowErrorDialog(true);
      handleCloseDialogs();
    }
  }, [courseReviewError]);

  const handleSubmitReview = (course) => {
    const reviewData = {
      rating: ratingValue,
      reviewText,
    };
    dispatch(addCourseReview(course.id, reviewData));
    handleCloseDialogs();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-pink-50 to-purple-100 py-10 px-4">
      <h1 className="text-4xl md:text-5xl font-extrabold text-center text-indigo-700 mb-10">
        🎓 My Enrolled Courses
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {enrolledCourses.map((course, index) => (
          <motion.div
            key={course.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-white/90 backdrop-blur-lg shadow-xl rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
            onClick={() => navigate(`/course-complete-detail/${course.id}`)}
          >
            <img
              src={course.thumbnailUrl}
              alt={course.name}
              className="h-48 w-full object-cover"
            />
            <div className="p-5 space-y-3">
              <h2 className="text-xl font-bold text-indigo-800 flex items-center gap-2">
                <FaBook className="text-blue-600" /> {course.name}
              </h2>

              <p className="text-gray-600 text-sm line-clamp-3">
                {course.description}
              </p>

              <div className="text-sm text-gray-700 space-y-1">
                <div className="flex items-center gap-2">
                  <FaClock className="text-pink-500" />
                  {course.durationInHours}h {course.durationInMinutes}m
                </div>
                <div className="flex items-center gap-2">
                  <FaChalkboardTeacher className="text-green-600" />
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/instructors/${course.instructorId}/${generateSlug(
                          course.instructor
                        )}`
                      );
                    }}
                    className="text-blue-600 hover:underline cursor-pointer"
                  >
                    {course.instructor}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FaLayerGroup className="text-purple-600" />
                  {course.category}
                </div>
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenReviewDialog(course.id);
                  }}
                >
                  <FaStar className="text-yellow-500" />
                  {course.averageRating.toFixed(1)} ⭐ ({course.totalReviews}{" "}
                  reviews)
                </div>
              </div>

              <div className="flex justify-start items-center mt-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDialog(course);
                  }}
                  className="group relative inline-flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-yellow-400 to-yellow-500 px-4 py-1.5 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:from-yellow-500 hover:to-yellow-600 hover:shadow-lg"
                >
                  <span className="absolute inset-0 scale-0 bg-white opacity-10 transition-all duration-300 group-hover:scale-100" />
                  ⭐ Rate Course
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 flex justify-center">
        <AppPagination setPage={setPage} totalPages={totalPages} />
      </div>

      {/* Course Reviews Dialog with Close Button */}
      <Dialog
        open={openReviewDialog}
        onClose={handleCloseReviewDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Course Reviews</span>
          <IconButton
            aria-label="close"
            onClick={handleCloseReviewDialog}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedCourseId && <CourseReviews courseId={selectedCourseId} />}
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog
        open={openCourseDialog}
        onClose={handleCloseDialogs}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="bg-indigo-100 text-indigo-900 font-bold">
          Rate "{selectedCourse?.name}"
        </DialogTitle>
        <DialogContent className="bg-white space-y-4 py-6">
          <div className="flex justify-center">
            <Rating
              name="course-rating"
              value={ratingValue}
              onChange={(e, newValue) => setRatingValue(newValue)}
              size="large"
              sx={{ cursor: "pointer" }}
              onClick={() => handleOpenReviewDialog(selectedCourse.id)}
            />
          </div>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Write your course review"
            variant="outlined"
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
          />
        </DialogContent>
        <DialogActions className="bg-indigo-50 py-4 px-6">
          <Button onClick={handleCloseDialogs} variant="outlined" color="error">
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmitReview(selectedCourse)}
            variant="contained"
            color="primary"
            disabled={ratingValue === 0 || reviewText.trim() === ""}
          >
            Submit Review
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        errorMessage={courseReviewError?.message}
      />
    </div>
  );
};

export default MyEnrolledCourses;
